import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

export default function UnsecuredDebtSection({
  data,
  onChange,
  isSpouse = false, isOpen, onOpen
}) {
  const [collapsed, setCollapsed] = useState(false);
  const prefix = isSpouse ? 'spouse' : '';

  const debtTypes = [
    { key: 'creditCardDebt', label: 'Credit Card' },
    { key: 'personalLoan', label: 'Personal Loan' },
    { key: 'medicalDebt', label: 'Medical' },
    { key: 'studentLoanDebt', label: 'Student Loan' },
    { key: 'backTaxDebt', label: 'Back Taxes' },
    { key: 'otherDebt', label: 'Other' },
  ];

  const getField = (type, suffix = '') => {
    if (!prefix) {
      // No spouse prefix: return type as-is (camelCase starting lowercase)
      return `${type}${suffix}`;
    }
    // Spouse prefix: capitalize the first letter of type, then prepend prefix
    const capitalized = type.charAt(0).toUpperCase() + type.slice(1);
    return `${prefix}${capitalized}${suffix}`;
  };

  const handleDebtChange = (type, index, field, value) => {
    const fieldName = getField(type, 'Details');
    const updated = [...(data[fieldName] || [])];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ target: { name: fieldName, value: updated } });
  };

  const addDebt = (type) => {
    const fieldName = getField(type, 'Details');
    const entry = type === 'backTaxDebt'
      ? { taxType: '', year: '', state: '', amount: '' }
      : type === 'otherDebt'
      ? { creditor: '', description: '', amountOwed: '' }
      : { creditor: '', debtBalance: '' };

    onChange({
      target: {
        name: fieldName,
        value: [...(data[fieldName] || []), entry],
      },
    });
  };

  const removeDebt = (type, index) => {
    const fieldName = getField(type, 'Details');
    const filtered = (data[fieldName] || []).filter((_, i) => i !== index);
    onChange({ target: { name: fieldName, value: filtered } });
  };

  const renderDebtFields = (type, debt, idx) => {
    switch (type) {
      case 'creditCardDebt':
      case 'personalLoan':
      case 'studentLoanDebt':
        return (
          <>
            <input
              className="input"
              placeholder="Creditor"
              value={debt.creditor || ''}
              onChange={(e) => handleDebtChange(type, idx, 'creditor', e.target.value)}
            />
            <input
              className="input"
              placeholder="Balance"
              value={debt.debtBalance || ''}
              onChange={(e) => handleDebtChange(type, idx, 'debtBalance', e.target.value)}
            />
          </>
        );
      case 'medicalDebt':
        return (
          <>
            <input
              className="input"
              placeholder="Creditor"
              value={debt.creditor || ''}
              onChange={(e) => handleDebtChange(type, idx, 'creditor', e.target.value)}
            />
            <input
              className="input"
              placeholder="Amount Owed"
              value={debt.amountOwed || ''}
              onChange={(e) => handleDebtChange(type, idx, 'amountOwed', e.target.value)}
            />
          </>
        );
      case 'backTaxDebt':
        return (
          <>
            <select
              className="input"
              value={debt.taxType || ''}
              onChange={(e) => handleDebtChange(type, idx, 'taxType', e.target.value)}
            >
              <option value="">Select Tax Type</option>
              <option value="Federal">Federal</option>
              <option value="State">State</option>
            </select>
            {debt.taxType === 'State' && (
              <input
                className="input"
                placeholder="State"
                value={debt.state || ''}
                onChange={(e) => handleDebtChange(type, idx, 'state', e.target.value)}
              />
            )}
            <input
              className="input"
              placeholder="Year"
              value={debt.year || ''}
              onChange={(e) => handleDebtChange(type, idx, 'year', e.target.value)}
            />
            <input
              className="input"
              placeholder="Amount"
              value={debt.amount || ''}
              onChange={(e) => handleDebtChange(type, idx, 'amount', e.target.value)}
            />
          </>
        );
      case 'otherDebt':
        return (
          <>
            <input
              className="input"
              placeholder="Creditor"
              value={debt.creditor || ''}
              onChange={(e) => handleDebtChange(type, idx, 'creditor', e.target.value)}
            />
            <input
              className="input"
              placeholder="Description"
              value={debt.description || ''}
              onChange={(e) => handleDebtChange(type, idx, 'description', e.target.value)}
            />
            <input
              className="input"
              placeholder="Amount Owed"
              value={debt.amountOwed || ''}
              onChange={(e) => handleDebtChange(type, idx, 'amountOwed', e.target.value)}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <section className="space-y-4">
      <div
        className="flex items-center justify-between bg-slate-100 px-4 py-2 rounded shadow-xl cursor-pointer"
        onClick={onOpen}
      >
        <h2 className="text-lg font-semibold text-gray-800">
          {isSpouse ? "Spouse Unsecured Debts" : "Unsecured Debts"}
        </h2>
        {isOpen ? (
          <ChevronRightIcon className="w-5 h-5 text-gray-600" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-600" />
        )}
      </div>

      {isOpen && (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {debtTypes.map(({ key, label }) => {
      const field = getField(key);
      const detailsField = getField(key, 'Details');
      const debts = data[detailsField] || [];

      return (
        <div key={key} className="bg-white p-4 rounded shadow-xl border space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="text-md font-medium text-gray-700">{label} Debts</h3>
            <button
              type="button"
              onClick={() => addDebt(key)}
              className="text-blue-600 text-sm"
            >
              + Add {label}
            </button>
          </div>
          {debts.map((debt, idx) => (
            <div key={idx} className="bg-gray-50 p-3 rounded border space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">
                  {label} Debt {idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeDebt(key, idx)}
                  className="text-red-500 text-sm"
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {renderDebtFields(key, debt, idx)}
              </div>
            </div>
          ))}
        </div>
      );
    })}
  </div>
)}
    </section>
  );
}